// src/hooks/usePushNotifications.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { urlBase64ToUint8Array } from '../utils/pushSubscription';
import { useUserStore } from '../store/userStore';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

if (!VAPID_PUBLIC_KEY) {
  console.error('[Push] VITE_VAPID_PUBLIC_KEY is missing — subscriptions will silently fail to deliver. Set it in Netlify env vars, matching the server-side VAPID_PUBLIC_KEY exactly.');
}
  
// Never let a browser/service-worker call hang the UI indefinitely.
// This is what previously left the sync modal stuck on screen with no
// way out - pushManager.subscribe() can hang on some Android/iOS PWA
// builds if the SW isn't fully warmed up yet.
const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
  ]);

/**
 * Single source of truth for push notification state.
 * Used by both the soft "enable notifications" nudge and the
 * Settings page toggle, so there's only one place that talks to
 * the service worker / Supabase.
 */
export function usePushNotifications() {
  const { currUserActive } = useUserStore();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default'); // 'default' | 'granted' | 'denied'
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'checking' | 'working' | 'error'
  const [error, setError] = useState(null);
  const isIOSNonStandaloneRef = useRef(false);

  // Feature-detect once.
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone =
      window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    isIOSNonStandaloneRef.current = isIOS && !isStandalone;

    const hasSupport =
      'serviceWorker' in navigator && 'PushManager' in window && !isIOSNonStandaloneRef.current;

    setSupported(hasSupport);
    if (hasSupport) setPermission(Notification.permission);
  }, []);

  const refreshSubscriptionStatus = useCallback(async () => {
    if (!supported || !currUserActive?.id) return;
    setStatus('checking');
    try {
      const registration = await withTimeout(
        navigator.serviceWorker.ready,
        5000,
        'Service worker not ready'
      );
      const sub = await registration.pushManager.getSubscription();
      if (!sub) {
        setIsSubscribed(false);
        setStatus('idle');
        return;
      }
      const { data } = await supabase
        .from('user_push_subscriptions')
        .select('endpoint')
        .eq('user_id', currUserActive.id)
        .eq('endpoint', sub.endpoint)
        .maybeSingle();
      setIsSubscribed(!!data);
      setStatus('idle');
    } catch (e) {
      console.error('[Push] status check failed:', e);
      setStatus('idle');
    }
  }, [supported, currUserActive?.id]);

  useEffect(() => {
    refreshSubscriptionStatus();
  }, [refreshSubscriptionStatus]);

  /**
   * Requests permission if needed, and (re)subscribes.
   * Returns { ok, reason } - reason is one of
   * 'unsupported' | 'denied' | 'default' | 'error' when !ok.
   */
  const subscribe = useCallback(async () => {
    if (!supported || !currUserActive?.id) return { ok: false, reason: 'unsupported' };

    setStatus('working');
    setError(null);
    try {
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== 'granted') {
        setStatus('idle');
        return { ok: false, reason: perm };
      }

      const registration = await withTimeout(
        navigator.serviceWorker.ready,
        8000,
        'Service worker not ready'
      );

      // Reuse an existing browser subscription if there is one instead of
      // tearing it down and recreating it - this was the main source of
      // unnecessary churn and stuck states in the old flow.
      let sub = await registration.pushManager.getSubscription();
      if (!sub) {
        sub = await withTimeout(
          registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          }),
          8000,
          'Subscription request timed out'
        );
      }

      const payload = JSON.parse(JSON.stringify(sub));
      const { error: dbError } = await supabase.from('user_push_subscriptions').upsert(
        { user_id: currUserActive.id, endpoint: payload.endpoint, subscription: payload },
        { onConflict: 'endpoint' }
      );
      if (dbError) throw dbError;

      setIsSubscribed(true);
      setStatus('idle');
      return { ok: true };
    } catch (e) {
      console.error('[Push] subscribe failed:', e);
      setError(e.message || 'Failed to enable notifications');
      setStatus('error');
      return { ok: false, reason: 'error', error: e };
    }
  }, [supported, currUserActive?.id]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    setStatus('working');
    try {
      const registration = await withTimeout(
        navigator.serviceWorker.ready,
        5000,
        'Service worker not ready'
      );
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        await supabase.from('user_push_subscriptions').delete().eq('endpoint', sub.endpoint);
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
      setStatus('idle');
      return { ok: true };
    } catch (e) {
      console.error('[Push] unsubscribe failed:', e);
      setError(e.message || 'Failed to turn off notifications');
      setStatus('error');
      return { ok: false, reason: 'error', error: e };
    }
  }, [supported]);

  return {
    supported,
    permission,
    isSubscribed,
    status,
    error,
    subscribe,
    unsubscribe,
    refreshSubscriptionStatus,
    isIOSNonStandalone: isIOSNonStandaloneRef.current,
  };
}
